<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue';
import RadButton from '../atoms/RadButton.vue';
import RadIcon from '../atoms/RadIcon.vue';

const props = defineProps<{
  isAuthenticated?: boolean;
  theme?: 'light' | 'dark' | string;
}>();

const emit = defineEmits<{
  (e: 'signIn'): void;
  (e: 'getStarted'): void;
  (e: 'goToDashboard'): void;
  (e: 'signOut'): void;
  (e: 'toggleTheme'): void;
}>();

const isScrolled = ref(false);
const internalIsDark = ref(true);

// Sync internal state with external prop if provided (for Main App)
watch(() => props.theme, (newTheme) => {
  if (newTheme !== undefined) {
    internalIsDark.value = newTheme === 'dark';
  }
}, { immediate: true });

const toggleTheme = () => {
  emit('toggleTheme'); // Always emit to parent
  // Fallback: Manage local state ONLY if no external theme prop is provided (for Storybook)
  if (props.theme === undefined) {
    internalIsDark.value = !internalIsDark.value;
    if (internalIsDark.value) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }
};

const handleScroll = () => {
  isScrolled.value = window.scrollY > 20;
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
  // Fallback: Initialize local state ONLY if no external theme prop is provided
  if (props.theme === undefined) {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      internalIsDark.value = savedTheme === 'dark';
    } else if (!window.matchMedia('(prefers-color-scheme: light)').matches) {
      internalIsDark.value = true;
    }

    if (internalIsDark.value) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});

const navLinks = [
  { label: 'Platform', href: '/platform' },
  { label: 'Features', href: '/features' },
  // { label: 'Pricing', href: '/pricing' },
];
</script>

<template>
  <nav
    class="fixed top-0 w-full z-50 transition-all duration-300 border-b border-white/5"
    :class="[
      isScrolled
        ? 'bg-white/80 dark:bg-slate-950/80 backdrop-blur-lg border-white/10 dark:border-white/10 py-3'
        : 'bg-transparent dark:bg-transparent border-transparent py-5 bg-white/50 dark:bg-slate-950/50',
      internalIsDark ? 'dark' : ''
    ]"
  >
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
      <!-- Logo -->
      <a href="/" class="flex items-center gap-0 group cursor-pointer !bg-transparent !no-underline !outline-none !border-none !text-inherit">
        <img src="/radsuite-logo.png" alt="RADSuite" class="h-5 w-auto block" :class="{ 'hidden': internalIsDark }" />
        <img src="/radsuite-logo-dark.png" alt="RADSuite" class="h-5 w-auto" :class="{ 'hidden': !internalIsDark }" />
        <span class="text-slate-900 dark:text-white font-bold text-xl tracking-tight">Suite</span>
      </a>

      <!-- Desktop Nav -->
      <div class="hidden md:flex items-center gap-8">
        <a
          v-for="link in navLinks"
          :key="link.label"
          :href="link.href"
          class="text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-rad-red dark:hover:text-rad-white transition-colors relative group"
        >
          {{ link.label }}
          <span class="absolute -bottom-1 left-0 w-0 h-0.5 bg-rad-red transition-all group-hover:w-full"></span>
        </a>
      </div>

      <!-- Actions -->
      <div class="flex items-center gap-4">
        <button
          @click="toggleTheme"
          class="p-2 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          aria-label="Toggle theme"
        >
          <RadIcon v-if="internalIsDark" name="Moon" :size="20" />
          <RadIcon v-else name="Sun" :size="20" />
        </button>

        <template v-if="!isAuthenticated">
          <button @click="$emit('signIn')" class="text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-rad-red dark:hover:text-rad-white transition-colors">
            Sign In
          </button>
          <RadButton @click="$emit('getStarted')" size="sm">
            Get Started
          </RadButton>
        </template>
        <template v-else>
          <button @click="$emit('goToDashboard')" class="text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-rad-red dark:hover:text-rad-white transition-colors">
            Dashboard
          </button>
          <RadButton @click="$emit('signOut')" size="sm" variant="outline">
            Sign Out
          </RadButton>
        </template>
      </div>
    </div>
  </nav>
</template>
